import {
  type AdminOrderDestination,
  ORDER_COURIERS,
  type OrderShipment,
  type ShipOrderInput,
  shipOrderInputSchema,
} from '@repo/api-contracts';
import { Plus } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button, OrderActionDialog } from '@/shared/ui';
import { OrderShipmentFields } from './order-shipment-fields';

interface OrderShipmentFormProps {
  readonly orderId: string;
  /** 주문 배송지(배송지 순서) — 1곳이면 자동 선택하고 선택 칸을 숨긴다. */
  readonly destinations: readonly AdminOrderDestination[];
  readonly onSubmit: (input: ShipOrderInput) => Promise<unknown>;
  readonly onReload: () => void;
  readonly onClose: () => void;
  readonly adding?: boolean;
  readonly existing?: OrderShipment[];
  readonly pending?: boolean;
}
const empty = (id: number, destinationId: string) => ({
  id,
  destinationId,
  kind: 'PARCEL' as 'PARCEL' | 'DIRECT',
  courierCode: '',
  trackingNumber: '',
  directMethodNote: '',
});
/**
 * 출고·송장 추가 — 송장 행(택배·직접 전달)을 하나 이상 받아 계약으로 검증하고, 같은 송장 중복 등록을 막는다(주문 전체 송장 기준).
 * 행마다 배송지(같은 주문의 배송지만 — 서버도 검사, ORDER_STATE_CONFLICT). 일부 배송지만 먼저 출고해도 된다.
 */
export function OrderShipmentForm({
  orderId,
  destinations,
  onSubmit,
  onReload,
  onClose,
  adding = false,
  existing = [],
  pending,
}: OrderShipmentFormProps) {
  const onlyDestinationId = destinations.length === 1 ? (destinations[0]?.id ?? '') : '';
  const [rows, setRows] = useState([empty(1, onlyDestinationId)]);
  const sequence = useRef(1);
  function parse() {
    return shipOrderInputSchema.safeParse({
      orderId,
      shipments: rows.map((row) =>
        row.kind === 'PARCEL'
          ? {
              kind: row.kind,
              courierCode: row.courierCode,
              trackingNumber: row.trackingNumber,
              destinationId: row.destinationId,
            }
          : {
              kind: row.kind,
              directMethodNote: row.directMethodNote,
              destinationId: row.destinationId,
            },
      ),
    });
  }
  function validate() {
    if (rows.some((row) => row.destinationId === '')) return '배송지를 선택해 주세요.';
    const result = parse();
    if (!result.success)
      return '송장 번호 6~30자리, 택배사 또는 방법 메모 1~200자를 입력해 주세요. 송장은 한 행 이상 필요합니다.';
    const keys = new Set(
      existing
        .filter((row) => row.kind === 'PARCEL')
        .map((row) => `${row.courierCode}:${row.trackingNumber}`),
    );
    for (const row of result.data.shipments) {
      if (row.kind === 'PARCEL') {
        if (!ORDER_COURIERS.some((courier) => courier.code === row.courierCode))
          return '택배사를 선택해 주세요.';
        const key = `${row.courierCode}:${row.trackingNumber}`;
        if (keys.has(key)) return '같은 송장을 중복 등록할 수 없습니다.';
        keys.add(key);
      }
    }
    return null;
  }
  return (
    <OrderActionDialog
      title={adding ? '송장 추가' : '출고'}
      submitLabel={adding ? '송장 추가' : '출고'}
      onReload={onReload}
      onClose={onClose}
      pending={pending}
      validate={validate}
      onSubmit={async () => {
        const result = parse();
        if (result.success) await onSubmit(result.data);
      }}
    >
      {rows.map((row, index) => (
        <OrderShipmentFields
          key={row.id}
          row={row}
          number={index + 1}
          destinations={destinations}
          onChange={(value) => setRows(rows.map((item) => (item.id === row.id ? value : item)))}
          onRemove={() => setRows(rows.filter((item) => item.id !== row.id))}
        />
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="self-start"
        onClick={() => {
          sequence.current += 1;
          setRows([...rows, empty(sequence.current, onlyDestinationId)]);
        }}
      >
        <Plus aria-hidden="true" />
        송장 행 추가
      </Button>
    </OrderActionDialog>
  );
}
